import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell, ReferenceLine
} from 'recharts';
import {
  TrendingUp, TrendingDown, DollarSign, FileText, Download,
  Plus, Trash2, Edit, Calendar, Filter, BarChart3,
  ArrowUpRight, ArrowDownRight, Receipt, Wallet,
  ChevronDown, X, Save, FileSpreadsheet, File, RefreshCw, RotateCw, Power
} from 'lucide-react';

const CATEGORY_LABELS = {
  loyer: "Loyer",
  fournisseurs: "Fournisseurs / Achats",
  salaires: "Salaires",
  charges_sociales: "Charges sociales",
  electricite: "Electricite / Eau",
  internet_telephone: "Internet / Telephone",
  transport: "Transport / Deplacement",
  fournitures: "Fournitures bureau",
  assurance: "Assurance",
  publicite: "Publicite / Marketing",
  divers: "Divers",
};

const CATEGORY_COLORS = {
  loyer: "#f59e0b",
  fournisseurs: "#3b82f6",
  salaires: "#8b5cf6",
  charges_sociales: "#ec4899",
  electricite: "#06b6d4",
  internet_telephone: "#10b981",
  transport: "#f97316",
  fournitures: "#6366f1",
  assurance: "#14b8a6",
  publicite: "#e11d48",
  divers: "#6b7280",
};

const PERIODS = [
  { key: 'all', label: 'Depuis le debut' },
  { key: 'year', label: 'Cette annee' },
  { key: 'quarter', label: 'Ce trimestre' },
  { key: 'month', label: 'Ce mois' },
  { key: 'custom', label: 'Personnalise' },
];

const MAGASIN_FILTERS = [
  { key: '', label: 'Tous (M1 + M2)' },
  { key: 'M1', label: 'M1' },
  { key: 'M2', label: 'M2' },
];

function getDateRange(period) {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  switch (period) {
    case 'month':
      return {
        date_debut: `${year}-${String(month + 1).padStart(2, '0')}-01`,
        date_fin: `${year}-${String(month + 1).padStart(2, '0')}-${new Date(year, month + 1, 0).getDate()}`
      };
    case 'quarter': {
      const q = Math.floor(month / 3);
      return {
        date_debut: `${year}-${String(q * 3 + 1).padStart(2, '0')}-01`,
        date_fin: `${year}-${String(q * 3 + 3).padStart(2, '0')}-${new Date(year, q * 3 + 3, 0).getDate()}`
      };
    }
    case 'year':
      return { date_debut: `${year}-01-01`, date_fin: `${year}-12-31` };
    case 'all':
    default:
      return { date_debut: '2020-01-01', date_fin: `${year}-12-31` };
  }
}

const ComptabilitePage = () => {
  const { api } = useAuth();
  const [period, setPeriod] = useState('all');
  const [magasinFilter, setMagasinFilter] = useState('');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [bilan, setBilan] = useState(null);
  const [depenses, setDepenses] = useState([]);
  const [revenus, setRevenus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bilan');
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [exportLoading, setExportLoading] = useState('');
  const [recurring, setRecurring] = useState([]);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [generatingRecurring, setGeneratingRecurring] = useState(false);
  const [facturesData, setFacturesData] = useState(null);

  const getRange = useCallback(() => {
    if (period === 'custom' && customStart && customEnd) {
      return { date_debut: customStart, date_fin: customEnd };
    }
    return getDateRange(period);
  }, [period, customStart, customEnd]);

  const magQuery = magasinFilter ? `&magasin_id=${encodeURIComponent(magasinFilter)}` : '';

  const fetchData = useCallback(async () => {
    setLoading(true);
    const range = getRange();
    const q = `date_debut=${range.date_debut}&date_fin=${range.date_fin}${magQuery}`;
    try {
      const [bilanRes, depensesRes, revenusRes] = await Promise.all([
        api.get(`/comptabilite/bilan?${q}`),
        api.get(`/comptabilite/depenses?${q}`),
        api.get(`/comptabilite/revenus?${q}`),
      ]);
      setBilan(bilanRes.data);
      setDepenses(depensesRes.data);
      setRevenus(revenusRes.data);
      // Fetch recurring expenses
      try {
        const recRes = await api.get('/comptabilite/depenses-recurrentes');
        setRecurring(recRes.data || []);
      } catch {}
      try {
        const facRes = await api.get(`/comptabilite/factures-acomptes?${q}`);
        setFacturesData(facRes.data);
      } catch {}
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api, getRange, magQuery]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = async (format) => {
    setExportLoading(format);
    const range = getRange();
    try {
      const res = await api.get(`/comptabilite/export/${format}?date_debut=${range.date_debut}&date_fin=${range.date_fin}${magQuery}`, { responseType: 'blob' });
      const ext = { excel: 'xlsx', csv: 'csv', pdf: 'pdf', fec: 'txt' }[format];
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `comptabilite_${range.date_debut}_${range.date_fin}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setExportLoading('');
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Supprimer cette depense ?')) return;
    try {
      await api.delete(`/comptabilite/depenses/${id}`);
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteRecurring = async (id) => {
    if (!window.confirm('Supprimer cette depense recurrente ?')) return;
    try {
      await api.delete(`/comptabilite/depenses-recurrentes/${id}`);
      fetchData();
    } catch (err) { console.error(err); }
  };

  const handleToggleRecurring = async (id, active) => {
    try {
      await api.put(`/comptabilite/depenses-recurrentes/${id}`, { active: !active });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const handleGenerateRecurring = async () => {
    setGeneratingRecurring(true);
    try {
      const res = await api.post('/comptabilite/depenses-recurrentes/generate');
      alert(`${res.data.generated} depense(s) generee(s)`);
      fetchData();
    } catch (err) { console.error(err); }
    finally { setGeneratingRecurring(false); }
  };

  const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n || 0);

  const maxBar = bilan?.mensuel?.length ? Math.max(...bilan.mensuel.map(m => Math.max(m.revenus, m.depenses)), 1) : 1;

  return (
    <Layout>
      <div className="space-y-6" data-testid="comptabilite-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white" data-testid="comptabilite-title">Suivi Comptable</h1>
            <p className="text-sm text-gray-500">Chiffre d'affaires, depenses et bilan</p>
            {!magasinFilter && bilan?.par_magasin && (
              <p className="text-xs text-amber-400/90 mt-1">Vue combinee — utilisez M1 / M2 pour isoler chaque magasin</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {MAGASIN_FILTERS.map((m) => (
              <button
                key={m.key || 'all'}
                type="button"
                onClick={() => setMagasinFilter(m.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
                  magasinFilter === m.key
                    ? 'text-white border-white/20'
                    : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10'
                }`}
                style={magasinFilter === m.key ? { background: 'var(--theme-primary)' } : {}}
                data-testid={`compta-magasin-${m.key || 'all'}`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {PERIODS.map(p => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  period === p.key ? 'text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
                style={period === p.key ? { background: 'var(--theme-primary)' } : {}}
                data-testid={`period-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Custom dates */}
        {period === 'custom' && (
          <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
            <Calendar className="w-4 h-4 text-gray-400" />
            <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
              className="bg-transparent border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white" data-testid="custom-start" />
            <span className="text-gray-500 text-sm">au</span>
            <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
              className="bg-transparent border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white" data-testid="custom-end" />
            <button onClick={fetchData} className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
              style={{ background: 'var(--theme-primary)' }} data-testid="custom-apply">Appliquer</button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="loading-spinner" /></div>
        ) : (
          <>
            {(bilan?.ventes_deduped > 0 || bilan?.par_magasin?.NON_ASSIGNE) && (
              <div
                className="rounded-xl border px-4 py-3 text-sm"
                style={{ borderColor: 'rgba(245, 158, 11, 0.35)', background: 'rgba(245, 158, 11, 0.08)' }}
                data-testid="compta-data-quality-banner"
              >
                <p className="font-semibold text-amber-200">Donnees corrigees automatiquement</p>
                <ul className="mt-1 text-xs text-amber-100/80 list-disc list-inside space-y-0.5">
                  {bilan?.ventes_deduped > 0 && (
                    <li>{bilan.ventes_deduped} vente(s) en double ignoree(s) (meme ticket POS compte une seule fois).</li>
                  )}
                  {bilan?.par_magasin?.NON_ASSIGNE?.total_revenus > 0 && (
                    <li>
                      {fmt(bilan.par_magasin.NON_ASSIGNE.total_revenus)} de revenus legacy sans magasin — voir onglet « Non assigne ».
                    </li>
                  )}
                </ul>
              </div>
            )}

            {!magasinFilter && bilan?.par_magasin && (
              <MagasinBreakdownPanel parMagasin={bilan.par_magasin} fmt={fmt} />
            )}

            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <KPICard label="Chiffre d'affaires" value={fmt(bilan?.total_revenus)} icon={TrendingUp} color="#22c55e" sub={`${bilan?.nb_transactions || 0} transactions`} testId="kpi-ca" />
              <KPICard label="Ventes boutique" value={fmt(bilan?.total_ventes)} icon={Receipt} color="var(--theme-primary)" testId="kpi-ventes" />
              <KPICard label="Reparations / Autres" value={fmt(bilan?.total_reparations)} icon={ArrowUpRight} color="#f59e0b" testId="kpi-reparations" />
              <KPICard label="Depenses" value={fmt(bilan?.total_depenses)} icon={ArrowDownRight} color="#ef4444" sub={`${bilan?.nb_depenses || 0} ecritures`} testId="kpi-depenses" />
            </div>

            {/* Net Result */}
            <div className="p-5 rounded-2xl border border-white/10" style={{ background: (bilan?.resultat_net || 0) >= 0 ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)', borderColor: (bilan?.resultat_net || 0) >= 0 ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Resultat Net</p>
                  <p className="text-3xl font-bold mt-1" data-testid="resultat-net"
                    style={{ color: (bilan?.resultat_net || 0) >= 0 ? '#22c55e' : '#ef4444' }}>
                    {fmt(bilan?.resultat_net)}
                  </p>
                </div>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{ background: (bilan?.resultat_net || 0) >= 0 ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)' }}>
                  {(bilan?.resultat_net || 0) >= 0 ? <TrendingUp className="w-7 h-7 text-green-400" /> : <TrendingDown className="w-7 h-7 text-red-400" />}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 p-1 rounded-xl bg-white/5 border border-white/10 w-fit flex-wrap">
              {['bilan', 'revenus', 'depenses', 'factures', 'recurrentes'].map(tab => (
                <button key={tab} onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === tab ? 'text-white shadow-lg' : 'text-gray-500 hover:text-gray-300'}`}
                  style={activeTab === tab ? { background: 'var(--theme-primary)' } : {}}
                  data-testid={`tab-${tab}`}>
                  {tab === 'bilan' ? 'Bilan mensuel' : tab === 'revenus' ? 'Revenus' : tab === 'depenses' ? 'Depenses' : tab === 'factures' ? 'Factures & Acomptes' : 'Recurrentes'}
                </button>
              ))}
            </div>

            {/* Tab content */}
            {activeTab === 'bilan' && <BilanTab bilan={bilan} maxBar={maxBar} fmt={fmt} />}
            {activeTab === 'revenus' && <RevenusTab revenus={revenus} fmt={fmt} />}
            {activeTab === 'depenses' && (
              <DepensesTab depenses={depenses} fmt={fmt} bilan={bilan}
                onAdd={() => { setEditingExpense(null); setShowExpenseModal(true); }}
                onEdit={(e) => { setEditingExpense(e); setShowExpenseModal(true); }}
                onDelete={handleDeleteExpense} />
            )}
            {activeTab === 'factures' && <FacturesAcomptesTab data={facturesData} fmt={fmt} />}
            {activeTab === 'recurrentes' && (
              <RecurrentesTab recurring={recurring} fmt={fmt}
                onAdd={() => setShowRecurringModal(true)}
                onDelete={handleDeleteRecurring}
                onToggle={handleToggleRecurring}
                onGenerate={handleGenerateRecurring}
                generating={generatingRecurring} />
            )}

            {/* Export buttons */}
            <div className="flex flex-wrap items-center gap-3 p-4 rounded-2xl bg-white/5 border border-white/10">
              <span className="text-sm font-semibold text-gray-400 mr-2"><Download className="w-4 h-4 inline mr-1" />Exporter :</span>
              {[
                { fmt: 'excel', label: 'Excel', icon: FileSpreadsheet, color: '#22c55e' },
                { fmt: 'csv', label: 'CSV', icon: FileText, color: '#3b82f6' },
                { fmt: 'pdf', label: 'PDF', icon: File, color: '#ef4444' },
                { fmt: 'fec', label: 'FEC', icon: FileText, color: '#f59e0b' },
              ].map(e => (
                <button key={e.fmt} onClick={() => handleExport(e.fmt)} disabled={!!exportLoading}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all border hover:scale-105"
                  style={{ borderColor: `${e.color}40`, background: `${e.color}15`, color: e.color }}
                  data-testid={`export-${e.fmt}`}>
                  {exportLoading === e.fmt ? <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" /> : <e.icon className="w-4 h-4" />}
                  {e.label}
                </button>
              ))}
            </div>
          </>
        )}

        {showExpenseModal && (
          <ExpenseModal
            expense={editingExpense}
            api={api}
            onClose={() => { setShowExpenseModal(false); setEditingExpense(null); }}
            onSaved={() => { setShowExpenseModal(false); setEditingExpense(null); fetchData(); }}
          />
        )}
        {showRecurringModal && (
          <RecurringExpenseModal
            api={api}
            onClose={() => setShowRecurringModal(false)}
            onSaved={() => { setShowRecurringModal(false); fetchData(); }}
          />
        )}
      </div>
    </Layout>
  );
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const KPICard = ({ label, value, icon: Icon, color, sub, testId }) => (
  <div className="p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-white/20 transition-colors" data-testid={testId}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs text-gray-500 font-medium">{label}</span>
      <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${color}20` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
    </div>
    <p className="text-xl font-bold text-white">{value}</p>
    {sub && <p className="text-[11px] text-gray-600 mt-1">{sub}</p>}
  </div>
);

const MagasinBreakdownPanel = ({ parMagasin, fmt }) => {
  const entries = Object.entries(parMagasin || {});
  if (entries.length === 0) return null;
  const labels = { M1: 'Magasin M1', M2: 'Magasin M2', NON_ASSIGNE: 'Non assigne (legacy)' };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="compta-par-magasin">
      {entries.map(([code, data]) => (
        <div
          key={code}
          className="p-4 rounded-2xl border border-white/10 bg-white/5"
          data-testid={`compta-magasin-card-${code}`}
        >
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">{labels[code] || code}</p>
          <p className="text-lg font-bold text-white mt-1">{fmt(data.total_revenus)}</p>
          <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] text-gray-500">
            <span>Ventes: {fmt(data.total_ventes)}</span>
            <span>Rep./autres: {fmt(data.total_reparations)}</span>
            <span>Depenses: {fmt(data.total_depenses)}</span>
            <span className={data.resultat_net >= 0 ? 'text-green-400' : 'text-red-400'}>
              Net: {fmt(data.resultat_net)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

const BilanTab = ({ bilan, maxBar, fmt }) => {
  // Prepare data for recharts
  const chartData = (bilan?.mensuel || []).map(m => ({
    name: m.mois,
    revenus: m.revenus || 0,
    depenses: m.depenses || 0,
    net: m.net || 0,
  }));

  return (
    <div className="space-y-4">
      {/* Revenue vs Expenses Chart */}
      <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
        <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
          <BarChart3 className="w-4 h-4" style={{ color: 'var(--theme-primary)' }} /> Revenus vs Depenses
        </h3>
        {chartData.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">Aucune donnee pour cette periode</p>
        ) : (
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                <Tooltip
                  contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '12px', color: '#f1f5f9', fontSize: 12 }}
                  formatter={(value) => [fmt(value)]}
                />
                <Legend wrapperStyle={{ fontSize: 11, color: '#94a3b8' }} />
                <Bar dataKey="revenus" name="Revenus" fill="#22c55e" radius={[4, 4, 0, 0]} />
                <Bar dataKey="depenses" name="Depenses" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Net Trend Line */}
      {chartData.length > 0 && (
        <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
          <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-blue-400" /> Tendance du resultat net
          </h3>
          <div style={{ width: '100%', height: 250 }}>
            <ResponsiveContainer>
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="netGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#7c3aed" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#7c3aed" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                <Tooltip
                  contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '12px', color: '#f1f5f9', fontSize: 12 }}
                  formatter={(value) => [fmt(value), 'Resultat net']}
                />
                <Area type="monotone" dataKey="net" stroke="#7c3aed" strokeWidth={2} fill="url(#netGrad)" />
                <ReferenceLine y={0} stroke="#475569" strokeDasharray="3 3" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Category Breakdown (Pie) */}
      {bilan?.depenses_par_categorie && Object.keys(bilan.depenses_par_categorie).length > 0 && (
        <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
          <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
            <Wallet className="w-4 h-4 text-amber-400" /> Repartition des depenses
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
            <div style={{ width: '100%', height: 250 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={Object.entries(bilan.depenses_par_categorie).map(([k, v]) => ({
                      name: CATEGORY_LABELS[k] || k,
                      value: v,
                      fill: CATEGORY_COLORS[k] || '#6b7280',
                    }))}
                    dataKey="value"
                    cx="50%" cy="50%"
                    innerRadius={50} outerRadius={90}
                    paddingAngle={2}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={{ stroke: '#475569' }}
                  >
                    {Object.entries(bilan.depenses_par_categorie).map(([k], i) => (
                      <Cell key={i} fill={CATEGORY_COLORS[k] || '#6b7280'} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '12px', color: '#f1f5f9', fontSize: 12 }}
                    formatter={(value) => [fmt(value)]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2">
              {Object.entries(bilan.depenses_par_categorie)
                .sort((a, b) => b[1] - a[1])
                .map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full shrink-0" style={{ background: CATEGORY_COLORS[k] || '#6b7280' }} />
                      <span className="text-gray-300">{CATEGORY_LABELS[k] || k}</span>
                    </div>
                    <span className="font-bold text-white">{fmt(v)}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const RevenusTab = ({ revenus, fmt }) => (
  <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
    <div className="p-4 border-b border-white/10 flex items-center justify-between">
      <h3 className="text-sm font-bold text-white">Detail des revenus</h3>
      <span className="text-xs text-gray-500">
        {revenus?.revenus?.length || 0} transactions — Total: {fmt(revenus?.total)}
        {revenus?.ventes_deduped > 0 ? ` · ${revenus.ventes_deduped} doublon(s) exclus` : ''}
      </span>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="revenus-table">
        <thead>
          <tr className="text-left text-xs text-gray-500 border-b border-white/10" style={{ background: 'rgba(var(--theme-primary-rgb), 0.08)' }}>
            <th className="px-4 py-3">Mag.</th>
            <th className="px-4 py-3">Date</th>
            <th className="px-4 py-3">Description</th>
            <th className="px-4 py-3">Client</th>
            <th className="px-4 py-3">Paiement</th>
            <th className="px-4 py-3 text-right">Montant</th>
          </tr>
        </thead>
        <tbody>
          {(!revenus?.revenus || revenus.revenus.length === 0) ? (
            <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Aucun revenu pour cette periode</td></tr>
          ) : revenus.revenus.map((r, i) => (
            <tr key={i} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
              <td className="px-4 py-3 text-gray-500 text-xs font-semibold">{r.magasin_id || '—'}</td>
              <td className="px-4 py-3 text-gray-400">{r.date?.slice(0, 10)}</td>
              <td className="px-4 py-3 text-white">{r.description}</td>
              <td className="px-4 py-3 text-gray-400">{r.client || '-'}</td>
              <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-white/10 text-gray-300">{r.mode_paiement || '-'}</span></td>
              <td className="px-4 py-3 text-right font-semibold text-green-400">{fmt(r.montant)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const DepensesTab = ({ depenses, fmt, bilan, onAdd, onEdit, onDelete }) => (
  <div className="space-y-4">
    {/* Category breakdown */}
    {bilan?.depenses_par_categorie && Object.keys(bilan.depenses_par_categorie).length > 0 && (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {Object.entries(bilan.depenses_par_categorie).sort((a, b) => b[1] - a[1]).map(([cat, montant]) => (
          <div key={cat} className="p-3 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: CATEGORY_COLORS[cat] || '#6b7280' }} />
              <span className="text-[11px] text-gray-400 truncate">{CATEGORY_LABELS[cat] || cat}</span>
            </div>
            <p className="text-sm font-bold text-white">{fmt(montant)}</p>
          </div>
        ))}
      </div>
    )}

    {/* Expense list */}
    <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <h3 className="text-sm font-bold text-white">Ecritures de depenses</h3>
        <button onClick={onAdd}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all"
          style={{ background: 'var(--theme-primary)' }}
          data-testid="add-expense-btn">
          <Plus className="w-3.5 h-3.5" /> Ajouter
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="depenses-table">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b border-white/10" style={{ background: 'rgba(var(--theme-primary-rgb), 0.08)' }}>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Categorie</th>
              <th className="px-4 py-3">Description</th>
              <th className="px-4 py-3">Ref.</th>
              <th className="px-4 py-3 text-right">Montant</th>
              <th className="px-4 py-3 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {depenses.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Aucune depense enregistree</td></tr>
            ) : depenses.map(e => (
              <tr key={e.id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                <td className="px-4 py-3 text-gray-400">{e.date}</td>
                <td className="px-4 py-3">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ background: CATEGORY_COLORS[e.categorie] || '#6b7280' }} />
                    <span className="text-white text-xs">{CATEGORY_LABELS[e.categorie] || e.categorie}</span>
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-300">{e.description || '-'}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{e.reference || '-'}</td>
                <td className="px-4 py-3 text-right font-semibold text-red-400">{fmt(e.montant)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button onClick={() => onEdit(e)} className="p-1.5 rounded-lg hover:bg-white/10 text-gray-500 hover:text-white transition-colors" data-testid={`edit-expense-${e.id}`}><Edit className="w-3.5 h-3.5" /></button>
                    <button onClick={() => onDelete(e.id)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-colors" data-testid={`delete-expense-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const ExpenseModal = ({ expense, api, onClose, onSaved }) => {
  const [date, setDate] = useState(expense?.date || new Date().toISOString().slice(0, 10));
  const [montant, setMontant] = useState(expense?.montant || '');
  const [categorie, setCategorie] = useState(expense?.categorie || 'divers');
  const [description, setDescription] = useState(expense?.description || '');
  const [reference, setReference] = useState(expense?.reference || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!montant || parseFloat(montant) <= 0) { setError('Montant invalide'); return; }
    setSaving(true);
    setError('');
    try {
      if (expense?.id) {
        await api.put(`/comptabilite/depenses/${expense.id}`, { date, montant: parseFloat(montant), categorie, description, reference });
      } else {
        await api.post('/comptabilite/depenses', { date, montant: parseFloat(montant), categorie, description, reference });
      }
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50" data-testid="expense-modal">
      <div className="bg-gray-900 border border-gray-700/50 rounded-2xl w-full max-w-md mx-4 shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-700/50">
          <h3 className="text-base font-bold text-white">{expense ? 'Modifier la depense' : 'Nouvelle depense'}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white" data-testid="expense-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/25 text-red-400 text-xs">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Date</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} required
                className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:border-white/30 outline-none" data-testid="expense-date" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Montant (EUR)</label>
              <input type="number" step="0.01" min="0.01" value={montant} onChange={e => setMontant(e.target.value)} required
                className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:border-white/30 outline-none" placeholder="0.00" data-testid="expense-montant" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Categorie</label>
            <select value={categorie} onChange={e => setCategorie(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:border-white/30 outline-none" data-testid="expense-categorie">
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Description</label>
            <input type="text" value={description} onChange={e => setDescription(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:border-white/30 outline-none" placeholder="Ex: Loyer mars 2026" data-testid="expense-description" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Reference (optionnel)</label>
            <input type="text" value={reference} onChange={e => setReference(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:border-white/30 outline-none" placeholder="N de facture..." data-testid="expense-reference" />
          </div>
          <button type="submit" disabled={saving}
            className="w-full py-3 rounded-lg font-semibold text-sm text-white transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: 'var(--theme-primary)' }} data-testid="expense-submit">
            {saving ? <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
            {expense ? 'Modifier' : 'Enregistrer'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ─── Recurring Expenses Tab ──────────────────────────────────────────────────

const RECURRENCE_LABELS = {
  hebdomadaire: 'Hebdomadaire',
  mensuel: 'Mensuel',
  trimestriel: 'Trimestriel',
  annuel: 'Annuel',
};

const RecurrentesTab = ({ recurring, fmt, onAdd, onDelete, onToggle, onGenerate, generating }) => (
  <div className="space-y-4">
    <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
      <div className="p-4 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-bold text-white flex items-center gap-2">
          <RotateCw className="w-4 h-4" style={{ color: 'var(--theme-primary)' }} /> Depenses recurrentes
        </h3>
        <div className="flex items-center gap-2">
          <button onClick={onGenerate} disabled={generating}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border border-white/10 hover:border-white/20 text-gray-300"
            data-testid="generate-recurring-btn">
            {generating ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Generer
          </button>
          <button onClick={onAdd}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all"
            style={{ background: 'var(--theme-primary)' }}
            data-testid="add-recurring-btn">
            <Plus className="w-3.5 h-3.5" /> Ajouter
          </button>
        </div>
      </div>
      {recurring.length === 0 ? (
        <div className="px-4 py-10 text-center text-gray-500 text-sm">
          Aucune depense recurrente configuree. Ajoutez vos charges fixes (loyer, abonnements, etc.)
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="recurring-table">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-white/10" style={{ background: 'rgba(var(--theme-primary-rgb), 0.08)' }}>
                <th className="px-4 py-3">Categorie</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">Frequence</th>
                <th className="px-4 py-3">Debut</th>
                <th className="px-4 py-3 text-right">Montant</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {recurring.map(r => (
                <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full" style={{ background: CATEGORY_COLORS[r.categorie] || '#6b7280' }} />
                      <span className="text-white text-xs">{CATEGORY_LABELS[r.categorie] || r.categorie}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-300">{r.description || '-'}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-purple-500/15 text-purple-300">
                      {RECURRENCE_LABELS[r.recurrence] || r.recurrence}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{r.start_date}</td>
                  <td className="px-4 py-3 text-right font-semibold text-red-400">{fmt(r.montant)}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => onToggle(r.id, r.active)}
                      className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${r.active ? 'bg-green-500/15 text-green-400' : 'bg-gray-500/15 text-gray-500'}`}
                      data-testid={`toggle-recurring-${r.id}`}>
                      {r.active ? 'Actif' : 'Pause'}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => onDelete(r.id)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-colors"
                      data-testid={`delete-recurring-${r.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </div>
);

const RecurringExpenseModal = ({ api, onClose, onSaved }) => {
  const [montant, setMontant] = useState('');
  const [categorie, setCategorie] = useState('loyer');
  const [description, setDescription] = useState('');
  const [reference, setReference] = useState('');
  const [recurrence, setRecurrence] = useState('mensuel');
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!montant || parseFloat(montant) <= 0) { setError('Montant invalide'); return; }
    setSaving(true);
    setError('');
    try {
      await api.post('/comptabilite/depenses-recurrentes', {
        montant: parseFloat(montant), categorie, description, reference,
        recurrence, start_date: startDate,
      });
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50" data-testid="recurring-expense-modal">
      <div className="bg-gray-900 border border-gray-700/50 rounded-2xl w-full max-w-md mx-4 shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-700/50">
          <h3 className="text-base font-bold text-white">Nouvelle depense recurrente</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white" data-testid="recurring-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/25 text-red-400 text-xs">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Montant (EUR)</label>
              <input type="number" step="0.01" min="0.01" value={montant} onChange={e => setMontant(e.target.value)} required
                className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:border-white/30 outline-none" placeholder="0.00" data-testid="recurring-montant" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Frequence</label>
              <select value={recurrence} onChange={e => setRecurrence(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:border-white/30 outline-none" data-testid="recurring-recurrence">
                {Object.entries(RECURRENCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Categorie</label>
            <select value={categorie} onChange={e => setCategorie(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:border-white/30 outline-none" data-testid="recurring-categorie">
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Description</label>
            <input type="text" value={description} onChange={e => setDescription(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:border-white/30 outline-none" placeholder="Ex: Loyer local commercial" data-testid="recurring-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Date de debut</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} required
                className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:border-white/30 outline-none" data-testid="recurring-start-date" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Reference (optionnel)</label>
              <input type="text" value={reference} onChange={e => setReference(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:border-white/30 outline-none" placeholder="N facture..." data-testid="recurring-reference" />
            </div>
          </div>
          <button type="submit" disabled={saving}
            className="w-full py-3 rounded-lg font-semibold text-sm text-white transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: 'var(--theme-primary)' }} data-testid="recurring-submit">
            {saving ? <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
            Enregistrer
          </button>
        </form>
      </div>
    </div>
  );
};

/* ─── Factures & Acomptes Tab ───────────────────────────────────────────── */
const FacturesAcomptesTab = ({ data, fmt }) => {
  if (!data) return <div className="py-12 text-center text-gray-500">Chargement...</div>;

  const formatDate = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return iso; }
  };

  return (
    <div className="space-y-5" data-testid="factures-acomptes-tab">
      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="p-4 rounded-xl bg-white/5 border border-white/10">
          <div className="text-xs text-gray-500 mb-1">Ventes encaissees</div>
          <div className="text-lg font-bold text-emerald-400">{fmt(data.total_ventes)}</div>
          <div className="text-[10px] text-gray-500">{data.nb_ventes} transaction{data.nb_ventes !== 1 ? 's' : ''}</div>
        </div>
        <div className="p-4 rounded-xl bg-white/5 border border-white/10">
          <div className="text-xs text-gray-500 mb-1">Acomptes encaisses</div>
          <div className="text-lg font-bold text-amber-400">{fmt(data.total_acomptes)}</div>
          <div className="text-[10px] text-gray-500">{data.nb_acomptes} versement{data.nb_acomptes !== 1 ? 's' : ''}</div>
        </div>
        <div className="p-4 rounded-xl bg-white/5 border border-white/10">
          <div className="text-xs text-gray-500 mb-1">Reste du global</div>
          <div className="text-lg font-bold text-red-400">{fmt(data.reste_du_global)}</div>
          <div className="text-[10px] text-gray-500">{data.nb_reparations_en_cours} reparation{data.nb_reparations_en_cours !== 1 ? 's' : ''} en cours</div>
        </div>
        <div className="p-4 rounded-xl bg-white/5 border border-white/10">
          <div className="text-xs text-gray-500 mb-1">CA total periode</div>
          <div className="text-lg font-bold text-cyan-400">{fmt((data.total_ventes || 0) + (data.total_acomptes || 0))}</div>
          <div className="text-[10px] text-gray-500">Ventes + Acomptes</div>
        </div>
      </div>

      {/* Modes de paiement */}
      {data.mode_paiement && Object.keys(data.mode_paiement).length > 0 && (
        <div className="p-4 rounded-xl bg-white/5 border border-white/10">
          <h4 className="text-sm font-bold text-gray-400 mb-3">Repartition par mode de paiement (acomptes)</h4>
          <div className="flex flex-wrap gap-3">
            {Object.entries(data.mode_paiement).map(([mode, info]) => (
              <div key={mode} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10">
                <div className="w-2 h-2 rounded-full" style={{ background: mode === 'Carte Bancaire' ? '#3b82f6' : mode === 'Virement' ? '#8b5cf6' : '#f59e0b' }}></div>
                <span className="text-xs text-gray-300 font-semibold">{mode}</span>
                <span className="text-xs text-gray-500">({info.count}x)</span>
                <span className="text-xs text-white font-bold">{fmt(info.total)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly acomptes bar chart */}
      {data.mensuel_acomptes?.length > 0 && (
        <div className="p-4 rounded-xl bg-white/5 border border-white/10">
          <h4 className="text-sm font-bold text-gray-400 mb-3">Evolution des acomptes par mois</h4>
          <div className="flex items-end gap-2 h-32">
            {data.mensuel_acomptes.map((m, i) => {
              const maxVal = Math.max(...data.mensuel_acomptes.map(x => x.total), 1);
              const height = Math.max((m.total / maxVal) * 100, 4);
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div className="text-[9px] text-amber-400 font-bold">{fmt(m.total)}</div>
                  <div className="w-full rounded-t-lg transition-all" style={{ height: `${height}%`, background: 'linear-gradient(to top, #f59e0b, #fbbf24)' }}></div>
                  <div className="text-[9px] text-gray-500">{m.mois.substring(5)}/{m.mois.substring(2, 4)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Reparations with remaining balance */}
      {data.devices_with_debt?.length > 0 && (
        <div className="p-4 rounded-xl bg-white/5 border border-white/10">
          <h4 className="text-sm font-bold text-gray-400 mb-3">Reparations avec solde restant</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 border-b border-white/10">
                  <th className="text-left py-2 px-2">Ref</th>
                  <th className="text-left py-2 px-2">Client</th>
                  <th className="text-left py-2 px-2">Modele</th>
                  <th className="text-right py-2 px-2">Prix total</th>
                  <th className="text-right py-2 px-2">Paye</th>
                  <th className="text-right py-2 px-2">Reste du</th>
                  <th className="text-center py-2 px-2">Statut</th>
                </tr>
              </thead>
              <tbody>
                {data.devices_with_debt.map(d => (
                  <tr key={d.ref} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-2 px-2 font-mono text-cyan-400 text-xs">{d.ref}</td>
                    <td className="py-2 px-2 text-white text-xs">{d.client_name}</td>
                    <td className="py-2 px-2 text-gray-400 text-xs">{d.model}</td>
                    <td className="py-2 px-2 text-right text-gray-300 text-xs">{fmt(d.price_total)}</td>
                    <td className="py-2 px-2 text-right text-emerald-400 text-xs">{fmt(d.total_acomptes)}</td>
                    <td className="py-2 px-2 text-right text-red-400 font-bold text-xs">{fmt(d.reste_a_payer)}</td>
                    <td className="py-2 px-2 text-center">
                      <span className="px-2 py-0.5 rounded-full text-[10px] bg-amber-500/20 text-amber-400">{d.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent acomptes */}
      {data.acomptes_recents?.length > 0 && (
        <div className="p-4 rounded-xl bg-white/5 border border-white/10">
          <h4 className="text-sm font-bold text-gray-400 mb-3">Derniers acomptes</h4>
          <div className="space-y-2">
            {data.acomptes_recents.map((a, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center">
                    <span className="text-amber-400 text-xs font-bold">AC</span>
                  </div>
                  <div>
                    <div className="text-xs text-white font-semibold">{a.device_ref || a.commande_id || '-'}</div>
                    <div className="text-[10px] text-gray-500">{a.mode_paiement} — {formatDate(a.date_versement)}</div>
                  </div>
                </div>
                <div className="text-sm font-bold text-amber-400">{fmt(a.montant)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


export default ComptabilitePage;